import React, { useState, useEffect, useRef } from 'react';
import s from './NovaApp.module.sass';
import { useAppContext } from '../../app-core/AppContext';
import {
    LayoutDashboard, TrendingUp, ChartCandlestick, Wallet, Briefcase, Mail, Settings,
    MessagesSquare, LogOut, PanelLeftClose, PanelLeftOpen,
    LineChart, ListChecks, History, Percent, Newspaper,
} from 'lucide-react';
import FlagIcon from '../../components/FlagIcon/FlagIcon';

const ICON_PROPS = { size: 21, strokeWidth: 1.8, 'aria-hidden': true };

// Эффект наведения доступен только на устройствах с настоящим курсором
const canHover = () =>
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(hover: hover) and (pointer: fine)').matches;

// «Magic highlight»: единый индикатор, который ПЛАВНО ПЕРЕТЕКАЕТ за курсором
// от пункта к пункту (как в навигации Vercel / Radix NavigationMenu), а не
// просто включается на каждом пункте отдельно. Реализация: один абсолютный
// «pill», который по наведению переезжает (translateY) и подстраивает высоту
// под наведённый пункт, а на уходе курсора — гаснет. Пункты помечены
// атрибутом data-nav-item.
function MagicHoverNav({ as: Tag = 'div', className, children }) {
    const ref = useRef(null);
    const pillRef = useRef(null);   // внешний слой — позиция (translateY/height)
    const fillRef = useRef(null);   // внутренний слой — «хвост» (растяжение scaleY)
    const lastTopRef = useRef(null);
    const fillAnimRef = useRef(null);

    const prefersReduced = () =>
        typeof window !== 'undefined' &&
        typeof window.matchMedia === 'function' &&
        window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const moveToItem = (item) => {
        const pill = pillRef.current;
        const fill = fillRef.current;
        const cont = ref.current;
        if (!pill || !cont || !item) return;
        const top = item.offsetTop;
        const h = item.offsetHeight;
        const hidden = pill.style.opacity === '' || pill.style.opacity === '0';

        if (hidden) {
            // Появляемся сразу НА нужном пункте — без «выезда» сверху и роста
            // от нулевой высоты: ставим позицию без анимации, затем плавно
            // проявляем (fade-in). Движение/хвост — только между пунктами.
            pill.style.transition = 'none';
            pill.style.transform = `translateY(${top}px)`;
            pill.style.height = `${h}px`;
            if (fill) fill.style.transform = 'scaleY(1)';
            void pill.offsetHeight; // форсим рефлоу, чтобы снять transition:none
            pill.style.transition = '';
            pill.style.opacity = '1';
            lastTopRef.current = top;
            return;
        }

        // Уже видимы → плавный переезд. «Хвостик»: во время движения капля
        // растягивается в сторону пути (origin на хвостовом краю), затем упруго
        // собирается обратно — как будто подсветка тянется следом за курсором.
        const prev = lastTopRef.current == null ? top : lastTopRef.current;
        const delta = top - prev;
        lastTopRef.current = top;

        pill.style.transform = `translateY(${top}px)`;
        pill.style.height = `${h}px`;

        if (fill && typeof fill.animate === 'function' && !prefersReduced() && delta !== 0) {
            // Выраженный «хвост»: капля вытягивается в сторону движения с
            // гарантированным пиком в середине пути (WAAPI-импульс scaleY),
            // затем упруго собирается обратно. Чем длиннее прыжок — тем сильнее.
            const dist = Math.abs(delta);
            const stretch = 1 + Math.min(dist / 120, 0.7);
            const squash = 1 - Math.min(dist / 600, 0.14); // лёгкое сжатие по ширине
            // origin на хвостовом крае: едем вниз → тянемся вверх (origin снизу)
            fill.style.transformOrigin = delta > 0 ? 'center bottom' : 'center top';
            if (fillAnimRef.current) fillAnimRef.current.cancel();
            fillAnimRef.current = fill.animate(
                [
                    { transform: 'scaleY(1) scaleX(1)' },
                    { transform: `scaleY(${stretch}) scaleX(${squash})`, offset: 0.4 },
                    { transform: 'scaleY(1) scaleX(1)' },
                ],
                { duration: 340, easing: 'cubic-bezier(0.23, 1, 0.32, 1)' }
            );
        }
    };

    const handleOver = (e) => {
        if (!canHover()) return;
        const item = e.target.closest('[data-nav-item]');
        if (item && ref.current && ref.current.contains(item)) moveToItem(item);
    };

    const handleLeave = () => {
        if (pillRef.current) pillRef.current.style.opacity = '0';
        if (fillAnimRef.current) fillAnimRef.current.cancel();
        if (fillRef.current) fillRef.current.style.transform = 'scaleY(1)';
        lastTopRef.current = null;
    };

    return (
        <Tag className={className} ref={ref} onMouseOver={handleOver} onMouseLeave={handleLeave}>
            <span className={s.navHoverPill} ref={pillRef} aria-hidden="true">
                <span className={s.navHoverPillFill} ref={fillRef} />
            </span>
            {children}
        </Tag>
    );
}

// «12» → «12px», «0.5em» → как есть (та же нормализация, что в оригинальном NavBar)
const withUnit = (v) => {
    if (!v || !String(v).trim()) return undefined;
    const value = String(v).trim();
    return (!isNaN(parseFloat(value)) && !value.match(/px|em|rem|pt|%/)) ? `${value}px` : value;
};

// Путь к логотипу: в конфиге хранится имя файла — нужен префикс /logo/
const resolveLogoSrc = (logo) => {
    if (!logo) return null;
    return (logo.startsWith('/') || logo.startsWith('http'))
        ? logo
        : `/logo/${encodeURIComponent(logo)}`;
};

export default function NovaNav() {
    const {
        showExactMenu, setShowExactMenu,
        isNavbarCollapsed, setIsNavbarCollapsed,
        siteSettings = {},
        showChat, setShowChat,
        userRole,
        openSiteSettings,
        safeT,
    } = useAppContext();

    const t = typeof safeT === 'function' ? safeT : (_k, f) => f;

    // Состав и порядок пунктов — 1:1 с оригинальным NavBar (включая
    // некликабельные «Рынки» и «Регион» и «Выйти», открывающий настройки сайта)
    const items = [
        { id: 'dashboard', label: t('navbar.dashboard', 'Главная'), Icon: LayoutDashboard },
        { id: 'trade', label: t('navbar.trading_section', 'Торговля'), Icon: TrendingUp },
        { id: 'markets', label: t('navbar.markets', 'Рынки'), Icon: ChartCandlestick, inert: true },
        // Новые пункты (пока только для вида): аналитика и ордера дополняют торговый блок
        { id: 'analytics', label: t('navbar.analytics', 'Аналитика'), Icon: LineChart, inert: true },
        { id: 'orders', label: t('navbar.orders', 'Ордера'), Icon: ListChecks, inert: true },
        { id: 'history', label: t('navbar.history', 'История'), Icon: History, inert: true },
        { id: 'wallet', label: t('navbar.wallet', 'Кошелек'), Icon: Wallet },
        { id: 'earn', label: t('navbar.earn', 'Стейкинг'), Icon: Percent, inert: true },
        { id: 'broker', label: t('navbar.broker', 'Брокер'), Icon: Briefcase },
        { id: 'news', label: t('navbar.news', 'Новости'), Icon: Newspaper, inert: true },
        { id: 'mailbox', label: t('navbar.mailbox', 'Почта'), Icon: Mail },
        { id: 'settings', label: t('navbar.settings', 'Настройки'), Icon: Settings },
    ];

    const brandName = siteSettings.siteName || 'Nova';

    // Кастомный шрифт названия из настроек сайта (сам шрифт загружает App.js)
    const brandNameStyle = {
        fontFamily: siteSettings.siteTitleFont || undefined,
        fontWeight: siteSettings.siteTitleFontWeight || undefined,
        fontStyle: siteSettings.siteTitleFontStyle || undefined,
        letterSpacing: withUnit(siteSettings.siteTitleLetterSpacing),
        fontSize: withUnit(siteSettings.siteTitleFontSize),
    };

    // Отступ между логотипом и названием — настраивается в Site Settings
    const brandGap = withUnit(siteSettings.siteTitleLogoGap);
    const brandStyle = brandGap ? { gap: brandGap } : undefined;

    // Логотип: /logo/... → запасной путь /data/logo/... → плейсхолдер
    const [logoFailed, setLogoFailed] = useState(false);
    const [logoSrc, setLogoSrc] = useState(() => resolveLogoSrc(siteSettings.siteLogo));
    useEffect(() => {
        setLogoSrc(resolveLogoSrc(siteSettings.siteLogo));
        setLogoFailed(false);
    }, [siteSettings.siteLogo]);
    const handleLogoError = () => {
        if (logoSrc && !logoSrc.includes('/data/logo/') && siteSettings.siteLogo) {
            setLogoSrc(`/data/logo/${encodeURIComponent(siteSettings.siteLogo)}`);
        } else {
            setLogoFailed(true);
        }
    };

    return (
        <aside className={s.nav}>
            <div className={s.navHead}>
                <div className={s.brand} style={brandStyle}>
                    {logoSrc && !logoFailed ? (
                        <img className={s.brandMark} src={logoSrc} alt="" onError={handleLogoError} />
                    ) : (
                        <span className={s.brandMark} aria-hidden="true" />
                    )}
                    {/* Класс обязан содержать «brandText»: живой предпросмотр
                        настроек сайта ищет элементы по [class*="brandText"] */}
                    <span className={s.brandText} style={brandNameStyle}>{brandName}</span>
                </div>
                <button
                    type="button"
                    className={s.collapseBtn}
                    onClick={() => setIsNavbarCollapsed(!isNavbarCollapsed)}
                    aria-label="Свернуть меню"
                >
                    {isNavbarCollapsed
                        ? <PanelLeftOpen size={17} strokeWidth={1.8} aria-hidden="true" />
                        : <PanelLeftClose size={17} strokeWidth={1.8} aria-hidden="true" />}
                </button>
            </div>

            <MagicHoverNav as="nav" className={s.navItems}>
                {items.map(it => (
                    <button
                        key={it.id}
                        type="button"
                        data-nav-item
                        data-nav-section={it.id}
                        className={`${s.navItem} ${showExactMenu === it.id ? s.navItemActive : ''} ${it.inert ? s.navItemInert : ''}`}
                        onClick={it.inert ? undefined : () => setShowExactMenu(it.id)}
                    >
                        <span className={s.navIcon}><it.Icon {...ICON_PROPS} /></span>
                        <span className={s.navLabel}>{it.label}</span>
                    </button>
                ))}
            </MagicHoverNav>

            <MagicHoverNav className={s.navFoot}>
                {/* Регион — некликабельный, с флагом из конфига (как в оригинале) */}
                <button
                    type="button"
                    data-nav-item
                    className={`${s.navItem} ${s.navItemInert}`}
                >
                    <span className={s.navIcon}>
                        <FlagIcon siteSettings={siteSettings} className={s.navFlag} alt="" />
                    </span>
                    <span className={s.navLabel}>{t('navbar.region', 'Регион')}</span>
                </button>
                <button
                    type="button"
                    data-nova-support
                    data-nav-item
                    className={`${s.navItem} ${showChat ? s.navItemActive : ''}`}
                    onClick={() => setShowChat(!showChat)}
                >
                    <span className={s.navIcon}><MessagesSquare {...ICON_PROPS} /></span>
                    <span className={s.navLabel}>{t('navbar.support', 'Поддержка')}</span>
                </button>
                {/* «Выйти» — как в оригинале открывает настройки сайта; для sap неактивен */}
                <button
                    type="button"
                    data-nav-item
                    className={`${s.navItem} ${userRole === 'sap' ? s.navItemInert : ''}`}
                    onClick={userRole === 'sap' ? undefined : openSiteSettings}
                >
                    <span className={s.navIcon}><LogOut {...ICON_PROPS} /></span>
                    <span className={s.navLabel}>{t('navbar.logout', 'Выйти')}</span>
                </button>
            </MagicHoverNav>
        </aside>
    );
}
